/**
 * NL-001 (2026-06-09) — route-level step gate. Wraps a page; if the
 * signed-in client's `current_step` is below `requiredStep`, renders
 * `<StepLockedCard fullPage />` instead. Coaches / head_coaches always
 * see the wrapped page (they're never step-gated — `useClientStep()`
 * returns `step: null` for them, which bypasses the gate).
 *
 * The gate waits for the step fetch to settle before deciding, so a
 * client never flashes the locked card before resolving and finding
 * out the section is in fact unlocked.
 */
import type { ReactNode } from 'react';

import { useClientStep } from '@/lib/use-client-step';
import { StepLockedCard, type LockedSectionKey } from './step-locked-card';

interface StepGateProps {
  readonly requiredStep: number;
  readonly sectionKey: LockedSectionKey;
  readonly children: ReactNode;
}

export function StepGate({
  requiredStep,
  sectionKey,
  children,
}: StepGateProps): JSX.Element {
  const { step, loading, isClient } = useClientStep();

  // Coach / head_coach always bypass.
  if (!isClient) return <>{children}</>;
  // Loading → render nothing so we don't flash either state. The
  // wrapped page also has its own loading affordances.
  if (loading) {
    return (
      <section
        className="max-w-3xl mx-auto px-s5 py-s7"
        data-testid={`step-gate-loading-${sectionKey}`}
        aria-hidden="true"
      />
    );
  }
  // For clients, default conservatively: if step couldn't be resolved
  // (network failure, parse error), render the locked card rather than
  // leak content that shouldn't be visible yet. The retry happens on
  // the next render via useClientStep().refresh().
  const effectiveStep = step ?? 0;
  if (effectiveStep < requiredStep) {
    return (
      <StepLockedCard
        sectionKey={sectionKey}
        unlocksAtStep={requiredStep}
        fullPage
      />
    );
  }
  return <>{children}</>;
}
